'use client'

import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import { useStytchMember } from '@stytch/nextjs/b2b'
import {
  establishPatientSessionExpiration,
  runSessionExpirationCleanupAndRedirect,
} from '@/utilities/auth/expireBrowserSession'
import { isClientPortal } from '@/utilities/stytch/config'
import {
  endImpersonationHandoff,
  getImpersonationHandoffExpectedMemberId,
  isImpersonationHandoffInProgress,
  subscribeImpersonationHandoff,
} from '@/utilities/stytch/impersonationHandoff'

interface UseSessionMonitorOptions {
  redirectTo?: string
  enabled?: boolean
}

export const useSessionMonitor = (options: UseSessionMonitorOptions = {}) => {
  const { redirectTo = '/login', enabled = true } = options
  const { member, isInitialized } = useStytchMember()
  const wasAuthenticatedRef = useRef<boolean>(false)
  const impersonationHandoffInProgress = useSyncExternalStore(
    subscribeImpersonationHandoff,
    isImpersonationHandoffInProgress,
    isImpersonationHandoffInProgress,
  )
  const impersonationHandoffExpectedMemberId = useSyncExternalStore(
    subscribeImpersonationHandoff,
    getImpersonationHandoffExpectedMemberId,
    getImpersonationHandoffExpectedMemberId,
  )

  const onSessionExpiration = useCallback(() => {
    if (isClientPortal()) {
      runSessionExpirationCleanupAndRedirect(redirectTo)
      return
    }
    establishPatientSessionExpiration(redirectTo)
  }, [redirectTo])

  useEffect(() => {
    // Early return if not enabled or not initialized. Also ignore member flicker
    // during Users-table impersonation handoff so we do not treat the swap as expiry.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- event work runs in an effect; keep until refactor
    if (!enabled || !isInitialized) return

    if (impersonationHandoffInProgress) {
      if (
        impersonationHandoffExpectedMemberId &&
        member?.member_id === impersonationHandoffExpectedMemberId
      ) {
        wasAuthenticatedRef.current = true
        endImpersonationHandoff()
      }
      return
    }

    // Track authentication state changes
    if (member && !wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = true
    } else if (!member && wasAuthenticatedRef.current) {
      // User was authenticated but now is not - session expired
      wasAuthenticatedRef.current = false
      onSessionExpiration()
    }
  }, [
    member,
    isInitialized,
    enabled,
    onSessionExpiration,
    impersonationHandoffInProgress,
    impersonationHandoffExpectedMemberId,
  ])
}
